import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box, Card, Typography, InputBase, InputAdornment, Grid, Chip, Stack, Button,
  Divider, CircularProgress, Alert, IconButton, List, ListItemButton, ListItemText,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import FormatListNumberedRoundedIcon from "@mui/icons-material/FormatListNumberedRounded";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
// Category identity icons — MUI outlined set (same library the rest
// of the app already ships) so no new dependency is introduced.
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import SmartphoneOutlinedIcon from "@mui/icons-material/SmartphoneOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import MoveToInboxOutlinedIcon from "@mui/icons-material/MoveToInboxOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import FamilyRestroomOutlinedIcon from "@mui/icons-material/FamilyRestroomOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  useGetHelpTreeQuery,
  useGetHelpArticleQuery,
  useListHelpArticlesQuery,
  HelpArticleListItem,
} from "../../Api/helpApi";
import useUser from "../../Core/hook/useUser";
import { Privileges } from "../../Core/Enum/enum";
import { API_URL } from "../../config";

const mediaUrl = (path: string): string => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  const base = String(API_URL || "").replace(/\/+$/, "");
  return `${base}/help/media/${path.replace(/^\/+/, "")}`;
};

const CATEGORY_ORDER = [
  "Getting Started",
  "Students & Parents",
  "Classes & Academics",
  "ABA Therapy",
  "Staff & HR",
  "Billing & Payments",
  "Communication",
  "Reports",
  "Administration",
  "Parent Portal",
  "System / Maintenance",
];

const HelpCenter: React.FC = () => {
  const { slug } = useParams<{ slug?: string }>();
  const navigate = useNavigate();
  const { userHasRight } = useUser();

  if (slug) return <ArticleView slug={slug} onBack={() => navigate("/help-center")} />;

  return <IndexView canManage={userHasRight(Privileges.HelpArticleCreate) || userHasRight(Privileges.HelpArticleEdit)} onOpen={(s) => navigate(`/help-center/${s}`)} onManage={() => navigate("/help-center/manage")} />;
};

const IndexView: React.FC<{ canManage: boolean; onOpen: (slug: string) => void; onManage: () => void }> = ({ canManage, onOpen, onManage }) => {
  const [q, setQ] = useState("");
  const treeRes = useGetHelpTreeQuery();
  const searchRes = useListHelpArticlesQuery(q.trim() ? { search: q.trim() } : undefined, { skip: !q.trim() });

  const tree = treeRes.data?.data?.tree ?? [];
  const results = searchRes.data?.data?.items ?? [];

  const sortedTree = useMemo(() => {
    return [...tree].sort((a, b) => {
      const ai = CATEGORY_ORDER.indexOf(a.category); const bi = CATEGORY_ORDER.indexOf(b.category);
      if (ai === -1 && bi === -1) return a.category.localeCompare(b.category);
      if (ai === -1) return 1; if (bi === -1) return -1;
      return ai - bi;
    });
  }, [tree]);

  // Aggregate article count — data-driven from the /help/tree
  // response, so the hero subtitle stays honest even when categories
  // are added/removed server-side.
  const totalArticles = sortedTree.reduce((n, c) => n + c.articles.length, 0);

  return (
    <Box sx={{
      // Full-width page container. The previous `maxWidth: 1160,
      // mx: "auto"` clamped the Help Center to a narrow centred
      // column, leaving huge empty margins on either side of the
      // main content area. The wrapper now takes the full width the
      // app shell allocates to page content and only reserves the
      // standard responsive page padding so the hero + category
      // grid stretch to the right edge like the Dashboard does.
      pb: 6,
      width: "100%",
      px: { xs: 2, sm: 2.5, md: 3, lg: 4 },
    }}>
      {/* ══════════ HERO — solid Step Up navy, no gradient ══════════
          Compact premium header. Search box is the primary action;
          the "Manage Documentation" CTA (admin-only) sits alongside
          the title so it doesn't push the hero taller. */}
      <Card sx={{
        p: { xs: 2.5, md: 3.5 }, mb: 3,
        bgcolor: "#173B6C", color: "#fff",
        borderRadius: 3, border: "1px solid #12305A",
        boxShadow: "0 8px 24px rgba(23,50,77,0.14)",
      }}>
        <Box sx={{
          display: "flex", alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "space-between", gap: 2, mb: 2.5,
          flexDirection: { xs: "column", md: "row" },
        }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: "-0.015em", mb: 0.5 }}>
              Help Center
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.82)", maxWidth: 620 }}>
              Short, practical guides for every module — with quick steps, screenshots and video walkthroughs.
            </Typography>
            {totalArticles > 0 && (
              <Typography sx={{
                mt: 1, fontSize: 12, fontWeight: 700, letterSpacing: "0.06em",
                textTransform: "uppercase", color: "#93C5FD",
              }}>
                {totalArticles} guide{totalArticles === 1 ? "" : "s"} · {sortedTree.length} categor{sortedTree.length === 1 ? "y" : "ies"}
              </Typography>
            )}
          </Box>
          {canManage && (
            <Button
              onClick={onManage}
              variant="contained"
              sx={{
                bgcolor: "#FFFFFF", color: "#173B6C",
                textTransform: "none", fontWeight: 700,
                borderRadius: 2, px: 2, py: 0.9, fontSize: 13.5,
                boxShadow: "0 4px 10px rgba(0,0,0,0.10)",
                flexShrink: 0,
                "&:hover": { bgcolor: "#E5EEFB", boxShadow: "0 6px 14px rgba(0,0,0,0.16)" },
              }}
            >
              Manage Documentation
            </Button>
          )}
        </Box>
        {/* ── Search field. Solid white surface with a strong search
             glyph and a bright brand focus ring. */}
        <Box sx={{
          display: "flex", alignItems: "center",
          bgcolor: "#FFFFFF",
          borderRadius: 2,
          px: 2, py: 0.5,
          border: "1px solid rgba(255,255,255,0.35)",
          transition: "box-shadow 160ms ease, border-color 160ms ease",
          "&:focus-within": {
            boxShadow: "0 0 0 4px rgba(147,197,253,0.45)",
            borderColor: "#93C5FD",
          },
        }}>
          <SearchIcon sx={{ color: "#2563EB", mr: 1, fontSize: 22 }} />
          <InputBase
            fullWidth
            placeholder="Search: create student, refund, staff schedule, add card..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            sx={{ fontSize: 15, color: "#172033", py: 1 }}
          />
        </Box>
      </Card>

      {q.trim() ? (
        <SearchResults items={results} loading={searchRes.isFetching} onOpen={onOpen} />
      ) : (
        <>
          {treeRes.isLoading && <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>}
          {treeRes.isError && <Alert severity="error">Could not load help articles.</Alert>}
          <Grid container spacing={2}>
            {sortedTree.map((cat) => (
              <Grid item xs={12} sm={6} md={4} key={cat.category}>
                <CategoryCard category={cat.category} articles={cat.articles} onOpen={onOpen} />
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
};

const SearchResults: React.FC<{ items: HelpArticleListItem[]; loading: boolean; onOpen: (slug: string) => void }> = ({ items, loading, onOpen }) => {
  if (loading) return <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={22} /></Stack>;
  if (!items.length) return <Alert severity="info">No articles match your search.</Alert>;
  return (
    <Card sx={{
      p: 1, borderRadius: 3,
      border: "1px solid #E2E8F0",
      boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 6px 16px rgba(15,23,42,0.04)",
    }}>
      <List dense disablePadding>
        {items.map((a) => {
          const { tone } = styleForCategory(a.category);
          const t = TONE_STYLES[tone];
          return (
            <ListItemButton
              key={a.id}
              onClick={() => onOpen(a.slug)}
              sx={{
                borderRadius: 1.5, mb: 0.25, py: 1.25,
                "&:hover": { bgcolor: t.softBg },
                "& .help-chevron": { color: "#94A3B8", transition: "color 140ms ease, transform 140ms ease" },
                "&:hover .help-chevron": { color: t.accent, transform: "translateX(2px)" },
              }}
            >
              <ListItemText
                primary={<Typography sx={{ fontWeight: 700, fontSize: 14.5, color: "#172033" }}>{a.title}</Typography>}
                secondary={
                  <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap", alignItems: "center" }}>
                    <Chip
                      size="small"
                      label={a.category}
                      sx={{
                        bgcolor: t.softBg, color: t.text,
                        border: `1px solid ${t.border}`,
                        fontWeight: 700, fontSize: 11,
                      }}
                    />
                    <Chip size="small" label={a.module} variant="outlined" sx={{ fontWeight: 600, fontSize: 11, color: "#64748B" }} />
                    {a.summary && <Typography variant="caption" sx={{ color: "#64748B" }}>{a.summary}</Typography>}
                  </Stack>
                }
                secondaryTypographyProps={{ component: "div" }}
              />
              <MediaBadges a={a} />
              <ChevronRightRoundedIcon className="help-chevron" sx={{ fontSize: 20, ml: 0.5 }} />
            </ListItemButton>
          );
        })}
      </List>
    </Card>
  );
};

// Help Center category variant system (2026-09 redesign).
//
// Every category renders with one of the tones below. `TONE_STYLES`
// is the shared token map (background wash for the icon block and
// hover surface, matching solid border, strong accent for the icon
// tile / chevron / title). `CATEGORY_STYLES` binds each backend
// category string to a { tone, icon } identity.
//
// Unknown categories fall back to a neutral slate identity via
// `styleForCategory`, so a new server-side category never renders
// as an un-themed generic card. NO gradients — every field is a
// solid hex.
type HelpTone =
  | "blue" | "royal" | "indigo" | "purple" | "violet"
  | "emerald" | "teal" | "cyan"
  | "amber" | "coral" | "rose" | "slate";

const TONE_STYLES: Record<HelpTone, {
  softBg: string; border: string; accent: string; text: string;
}> = {
  blue:    { softBg: "#EFF6FF", border: "#BFDBFE", accent: "#2563EB", text: "#1E3A8A" },
  royal:   { softBg: "#EEF4FF", border: "#B7CCFB", accent: "#1D4ED8", text: "#1E3A8A" },
  indigo:  { softBg: "#EEF2FF", border: "#C7D2FE", accent: "#4F46E5", text: "#312E81" },
  purple:  { softBg: "#F5F3FF", border: "#DDD6FE", accent: "#7C3AED", text: "#4C1D95" },
  violet:  { softBg: "#F5F3FF", border: "#DDD6FE", accent: "#8B5CF6", text: "#4C1D95" },
  emerald: { softBg: "#ECFDF5", border: "#A7F3D0", accent: "#16A34A", text: "#065F46" },
  teal:    { softBg: "#F0FDFA", border: "#99F6E4", accent: "#0F9F9A", text: "#134E4A" },
  cyan:    { softBg: "#ECFEFF", border: "#A5F3FC", accent: "#0891B2", text: "#155E75" },
  amber:   { softBg: "#FFFBEB", border: "#FDE68A", accent: "#D97706", text: "#78350F" },
  coral:   { softBg: "#FFF1F2", border: "#FECDD3", accent: "#E14D5A", text: "#881337" },
  rose:    { softBg: "#FFF1F2", border: "#FECDD3", accent: "#F43F5E", text: "#881337" },
  slate:   { softBg: "#F8FAFC", border: "#CBD5E1", accent: "#475569", text: "#0F172A" },
};

// Icon type — MUI SvgIcon-compatible.
type IconComponent = React.ComponentType<{ sx?: any; fontSize?: any }>;

const CATEGORY_STYLES: Record<string, { tone: HelpTone; icon: IconComponent }> = {
  // Parent-portal categories (screenshot):
  "Getting Started":       { tone: "blue",    icon: RocketLaunchOutlinedIcon },
  "Assessments":           { tone: "purple",  icon: AssignmentTurnedInOutlinedIcon },
  "Attendance":            { tone: "emerald", icon: EventAvailableOutlinedIcon },
  "Classes & Timetable":   { tone: "indigo",  icon: CalendarMonthOutlinedIcon },
  "Home Screen Access":    { tone: "teal",    icon: SmartphoneOutlinedIcon },
  "Messages":              { tone: "violet",  icon: ChatBubbleOutlineIcon },
  "Payments":              { tone: "amber",   icon: PaymentsOutlinedIcon },
  "Report Cards":          { tone: "royal",   icon: DescriptionOutlinedIcon },
  "Requests":              { tone: "coral",   icon: MoveToInboxOutlinedIcon },
  // Backend canonical categories (from CATEGORY_ORDER):
  "Students & Parents":    { tone: "cyan",    icon: GroupsOutlinedIcon },
  "Classes & Academics":   { tone: "indigo",  icon: MenuBookOutlinedIcon },
  "ABA Therapy":           { tone: "violet",  icon: PsychologyOutlinedIcon },
  "Staff & HR":            { tone: "amber",   icon: BadgeOutlinedIcon },
  "Billing & Payments":    { tone: "emerald", icon: PaymentsOutlinedIcon },
  "Communication":         { tone: "rose",    icon: ForumOutlinedIcon },
  "Reports":               { tone: "royal",   icon: BarChartOutlinedIcon },
  "Administration":        { tone: "slate",   icon: SettingsOutlinedIcon },
  "Parent Portal":         { tone: "purple",  icon: FamilyRestroomOutlinedIcon },
  "System / Maintenance":  { tone: "slate",   icon: BuildOutlinedIcon },
};

const styleForCategory = (cat: string): { tone: HelpTone; icon: IconComponent } =>
  CATEGORY_STYLES[cat] ?? { tone: "slate", icon: FolderOutlinedIcon };

const CategoryCard: React.FC<{ category: string; articles: HelpArticleListItem[]; onOpen: (slug: string) => void }> = ({ category, articles, onOpen }) => {
  const { tone, icon: Icon } = styleForCategory(category);
  const t = TONE_STYLES[tone];
  return (
    <Card
      elevation={0}
      sx={{
        // White card body with a tone-tinted border and a solid
        // tone-coloured top accent stripe so each card carries a
        // clear identity without becoming a saturated block.
        position: "relative",
        p: 0,
        borderRadius: 3,
        height: "100%",
        bgcolor: "#FFFFFF",
        border: `1px solid ${t.border}`,
        boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 6px 16px rgba(15,23,42,0.04)",
        overflow: "hidden",
        transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
        "&:hover": {
          transform: "translateY(-2px)",
          borderColor: t.accent,
          boxShadow: `0 2px 6px rgba(15,23,42,0.05), 0 12px 24px rgba(15,23,42,0.08)`,
        },
      }}
    >
      {/* Solid tone accent stripe along the top. */}
      <Box sx={{ height: 4, bgcolor: t.accent }} />

      {/* ── Header — icon block + category title + article count. */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2.25, pt: 2, pb: 1.25 }}>
        <Box sx={{
          width: 44, height: 44, borderRadius: "12px",
          bgcolor: t.accent, color: "#FFFFFF",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
          "& svg": { fontSize: 22 },
        }}>
          <Icon />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 15, color: "#172033", lineHeight: 1.15, letterSpacing: "-0.01em" }}>
            {category}
          </Typography>
          <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#64748B", mt: 0.35, letterSpacing: "0.04em", textTransform: "uppercase" }}>
            {articles.length} article{articles.length !== 1 ? "s" : ""}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: t.border, mx: 2.25 }} />

      {/* ── Article rows — clearly clickable with a chevron affordance
           and a tone-tinted hover surface. */}
      <Stack spacing={0} sx={{ px: 1.25, py: 1.25 }}>
        {articles.slice(0, 8).map((a) => (
          <Box
            key={a.id}
            role="button"
            tabIndex={0}
            onClick={() => onOpen(a.slug)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(a.slug); }
            }}
            sx={{
              display: "flex", alignItems: "center", gap: 1,
              cursor: "pointer",
              px: 1.25, py: 1,
              borderRadius: 1.5,
              transition: "background-color 140ms ease",
              "& .help-chevron": {
                color: "#94A3B8",
                transition: "color 140ms ease, transform 140ms ease",
              },
              "&:hover": {
                bgcolor: t.softBg,
                "& .help-chevron": { color: t.accent, transform: "translateX(2px)" },
                "& .help-title": { color: t.text },
              },
              "&:focus-visible": {
                outline: `2px solid ${t.accent}`,
                outlineOffset: 2,
              },
            }}
          >
            <Typography
              className="help-title"
              sx={{
                fontWeight: 600, fontSize: 13.5, color: "#172033",
                flex: 1, minWidth: 0,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                transition: "color 140ms ease",
              }}
              title={a.title}
            >
              {a.title}
            </Typography>
            <MediaBadges a={a} compact />
            <ChevronRightRoundedIcon className="help-chevron" sx={{ fontSize: 18 }} />
          </Box>
        ))}
        {articles.length > 8 && (
          <Typography sx={{ fontSize: 11.5, color: "#64748B", pt: 0.75, pl: 1.25, fontWeight: 600 }}>
            +{articles.length - 8} more
          </Typography>
        )}
      </Stack>
    </Card>
  );
};

const MediaBadges: React.FC<{ a: HelpArticleListItem; compact?: boolean }> = ({ a, compact }) => (
  <Stack direction="row" spacing={0.5} sx={{ color: "text.disabled" }}>
    {a.has_quick && <FormatListNumberedRoundedIcon fontSize="small" />}
    {a.has_images && <PhotoLibraryOutlinedIcon fontSize="small" />}
    {a.has_video && <PlayCircleOutlineIcon fontSize="small" />}
    {a.has_full && !compact && <ArticleOutlinedIcon fontSize="small" />}
  </Stack>
);

// Shared visual language for every article section card. White
// surface + subtle border/shadow + a strong title row with an
// optional tone-tinted eyebrow icon on the left. Kept in one place
// so the Quick Guide, Screenshots, Video, and Full Guide sections
// all render identically without local duplication.
const SectionCard: React.FC<{
  id?: string;
  icon?: React.ReactNode;
  toneAccent?: string;
  title: string;
  children: React.ReactNode;
}> = ({ id, icon, toneAccent, title, children }) => (
  <Card
    id={id}
    elevation={0}
    sx={{
      p: { xs: 2.5, md: 3.5 },
      mb: 2.5,
      borderRadius: 3,
      border: "1px solid #E5EAF1",
      boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 6px 16px rgba(15,23,42,0.04)",
      bgcolor: "#FFFFFF",
      scrollMarginTop: 96, // anchor jumps land below the sticky app bar
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
      {icon && (
        <Box sx={{
          width: 34, height: 34, borderRadius: "10px",
          bgcolor: toneAccent ?? "#2563EB", color: "#FFFFFF",
          display: "flex", alignItems: "center", justifyContent: "center",
          "& svg": { fontSize: 18 },
        }}>
          {icon}
        </Box>
      )}
      <Typography sx={{ fontSize: { xs: 17, md: 18 }, fontWeight: 800, color: "#172033", letterSpacing: "-0.01em" }}>
        {title}
      </Typography>
    </Box>
    {children}
  </Card>
);

// Nice "N min ago / N days ago / MMM D, YYYY" for the updated_at
// meta pill. Falls back to null when the date can't be parsed so
// the caller can skip the pill entirely rather than render "Invalid".
const relativeDateLabel = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const diffMs = Date.now() - d.getTime();
  const day = 86400000;
  if (diffMs < day) return "Updated today";
  if (diffMs < 7 * day) {
    const n = Math.floor(diffMs / day);
    return `Updated ${n} day${n === 1 ? "" : "s"} ago`;
  }
  return `Updated ${d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
};

const ArticleView: React.FC<{ slug: string; onBack: () => void }> = ({ slug, onBack }) => {
  const { data, isLoading, isError } = useGetHelpArticleQuery(slug);
  const article = data?.data?.article ?? null;

  // Category tone identity — reuses the same variant map as the
  // Help Center landing page so the article hero badge and the
  // landing-page card colour agree.
  const { tone: catTone } = article
    ? styleForCategory(article.category)
    : { tone: "slate" as HelpTone };
  const t = TONE_STYLES[catTone];

  // Runtime capability flags — sections render only when the data
  // has real content behind them, so the "On this page" aside never
  // advertises an empty jump target.
  const hasQuick  = !!(article?.quick_steps?.length);
  const hasImages = !!(article?.images?.length);
  const hasVideo  = !!(article?.video_url || article?.video_storage_path);
  const hasFull   = !!article?.body_markdown;

  const jumpItems: { id: string; label: string }[] = [];
  if (hasQuick)  jumpItems.push({ id: "quick-guide",  label: "Quick Guide" });
  if (hasImages) jumpItems.push({ id: "screenshots",  label: "Screenshots" });
  if (hasVideo)  jumpItems.push({ id: "video",        label: "Video walkthrough" });
  if (hasFull)   jumpItems.push({ id: "full-guide",   label: "Full Guide" });

  const updatedLabel = relativeDateLabel(article?.updated_at);

  return (
    <Box sx={{
      // Full-width page container — matches the landing page so
      // hero + article body extend to the same right edge (the
      // former maxWidth: 900 / mx: auto column is gone).
      pb: 6,
      width: "100%",
      px: { xs: 2, sm: 2.5, md: 3, lg: 4 },
    }}>
      {/* ══════════ Top utility row — Back to Help Center ══════════ */}
      <Button
        startIcon={<ArrowBackRoundedIcon />}
        onClick={onBack}
        sx={{
          textTransform: "none", fontWeight: 700, fontSize: 13.5,
          color: "#2563EB",
          bgcolor: "transparent",
          borderRadius: 1.5,
          px: 1.25, py: 0.5, mb: 2, ml: -0.5,
          "&:hover": { bgcolor: "#EFF6FF" },
          "&:focus-visible": { outline: "2px solid #2563EB", outlineOffset: 2 },
        }}
      >
        Back to Help Center
      </Button>

      {isLoading && <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>}
      {isError && <Alert severity="error">Could not load this article.</Alert>}

      {article && (
        <Box sx={{
          // 2-column shell on desktop: main article column + a
          // narrow sticky aside. Collapses to a single column on
          // tablets / mobile so the reading order and content stay
          // identical on small screens.
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: jumpItems.length > 0 || updatedLabel ? "minmax(0, 1fr) 280px" : "1fr",
          },
          gap: { xs: 0, lg: 3 },
          alignItems: "start",
        }}>
          {/* ══════════ LEFT — Article body ══════════ */}
          <Box sx={{ minWidth: 0 }}>
            {/* ── Hero / overview card ─────────────────────────── */}
            <Card
              elevation={0}
              sx={{
                position: "relative",
                p: { xs: 3, md: 4 },
                mb: 2.5,
                borderRadius: 3,
                bgcolor: "#FFFFFF",
                border: `1px solid ${t.border}`,
                boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 8px 20px rgba(15,23,42,0.05)",
                overflow: "hidden",
              }}
            >
              {/* Solid tone accent stripe (identity marker). */}
              <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, bgcolor: t.accent }} />

              <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: "wrap", rowGap: 1 }}>
                <Chip
                  size="small"
                  label={article.category}
                  sx={{
                    bgcolor: t.softBg, color: t.text,
                    border: `1px solid ${t.border}`,
                    fontWeight: 800, fontSize: 11.5, height: 24, letterSpacing: 0.2,
                  }}
                />
                <Chip
                  size="small"
                  label={article.module}
                  variant="outlined"
                  sx={{
                    borderColor: "#E5EAF1", color: "#64748B",
                    fontWeight: 700, fontSize: 11.5, height: 24,
                  }}
                />
                {!article.is_published && (
                  <Chip
                    size="small"
                    label="Draft"
                    sx={{
                      bgcolor: "#FFFBEB", color: "#78350F",
                      border: "1px solid #FDE68A",
                      fontWeight: 800, fontSize: 11.5, height: 24,
                    }}
                  />
                )}
              </Stack>

              <Typography sx={{
                fontSize: { xs: 26, md: 32 },
                fontWeight: 800, letterSpacing: "-0.02em",
                color: "#172033", lineHeight: 1.15, mb: article.summary ? 1.25 : 0,
              }}>
                {article.title}
              </Typography>
              {article.summary && (
                <Typography sx={{ fontSize: { xs: 15, md: 16 }, color: "#334155", lineHeight: 1.65, maxWidth: 780 }}>
                  {article.summary}
                </Typography>
              )}

              {/* Data-driven metadata row — every field is shown only
                  if the underlying data has it. No invented stats. */}
              {(updatedLabel || hasQuick || hasImages || hasVideo || hasFull) && (
                <Stack direction="row" spacing={2} sx={{ mt: 2.5, pt: 2, borderTop: "1px dashed #E5EAF1", flexWrap: "wrap", rowGap: 1, color: "#64748B", fontSize: 12.5, fontWeight: 600 }}>
                  {updatedLabel && <Box>{updatedLabel}</Box>}
                  {hasQuick  && <Box>· {article.quick_steps.length} quick step{article.quick_steps.length === 1 ? "" : "s"}</Box>}
                  {hasImages && <Box>· {article.images.length} screenshot{article.images.length === 1 ? "" : "s"}</Box>}
                  {hasVideo  && <Box>· Video walkthrough</Box>}
                  {hasFull   && <Box>· Full guide</Box>}
                </Stack>
              )}
            </Card>

            {/* ── Quick Guide ──────────────────────────────────── */}
            {hasQuick && (
              <SectionCard
                id="quick-guide"
                title="Quick Guide"
                icon={<FormatListNumberedRoundedIcon />}
                toneAccent={t.accent}
              >
                <Box component="ol" sx={{ pl: 0, m: 0, listStyle: "none" }}>
                  {article.quick_steps.map((s, i) => (
                    <Box
                      key={s.number}
                      sx={{
                        display: "flex", gap: 2, alignItems: "flex-start",
                        py: 2,
                        borderTop: i === 0 ? "none" : "1px solid #F1F5F9",
                      }}
                    >
                      <Box sx={{
                        width: 36, height: 36, borderRadius: "50%",
                        flexShrink: 0,
                        bgcolor: t.accent, color: "#FFFFFF",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 14, fontWeight: 800,
                        boxShadow: `0 3px 8px ${t.accent}40`,
                      }}>
                        {s.number}
                      </Box>
                      <Box sx={{ pt: 0.4, minWidth: 0, flex: 1 }}>
                        {s.title && (
                          <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#172033", mb: 0.5, lineHeight: 1.3 }}>
                            {s.title}
                          </Typography>
                        )}
                        <Typography sx={{ fontSize: 14, color: "#334155", lineHeight: 1.75 }}>
                          {s.description}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </SectionCard>
            )}

            {/* ── Screenshots ─────────────────────────────────── */}
            {hasImages && (
              <SectionCard
                id="screenshots"
                title="Screenshots"
                icon={<PhotoLibraryOutlinedIcon />}
                toneAccent={t.accent}
              >
                <Stack spacing={3}>
                  {[...article.images].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0)).map((img, i) => (
                    <Box key={i}>
                      <Box sx={{
                        position: "relative",
                        borderRadius: 2, overflow: "hidden",
                        border: "1px solid #E5EAF1",
                        bgcolor: "#F8FAFC",
                      }}>
                        <Box component="img" src={mediaUrl(img.path)} alt={img.caption ?? ""} sx={{ display: "block", width: "100%", height: "auto" }} />
                        {(img.markers ?? []).map((m) => (
                          <Box key={m.number} sx={{
                            position: "absolute", left: `${m.x * 100}%`, top: `${m.y * 100}%`,
                            transform: "translate(-50%,-50%)",
                            width: 30, height: 30, borderRadius: "50%",
                            bgcolor: t.accent, color: "#FFFFFF",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 13, fontWeight: 800,
                            boxShadow: `0 3px 10px ${t.accent}66`,
                            border: "2px solid white",
                          }}>
                            {m.number}
                          </Box>
                        ))}
                      </Box>
                      {img.caption && (
                        <Typography sx={{ mt: 1, fontSize: 13, color: "#64748B" }}>
                          {img.caption}
                        </Typography>
                      )}
                      {(img.markers ?? []).length > 0 && (
                        <Box component="ol" sx={{ pl: 3, mt: 1.25 }}>
                          {[...(img.markers ?? [])].sort((a, b) => a.number - b.number).map((m) => (
                            <Typography key={m.number} component="li" sx={{ fontSize: 13.5, color: "#334155", mb: 0.75, lineHeight: 1.6 }}>
                              {m.text}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Stack>
              </SectionCard>
            )}

            {/* ── Video walkthrough ───────────────────────────── */}
            {hasVideo && (
              <SectionCard
                id="video"
                title="Video walkthrough"
                icon={<PlayCircleOutlineIcon />}
                toneAccent={t.accent}
              >
                <VideoBox article={article as any} />
              </SectionCard>
            )}

            {/* ── Full Guide ──────────────────────────────────── */}
            {hasFull && (
              <SectionCard
                id="full-guide"
                title="Full Guide"
                icon={<ArticleOutlinedIcon />}
                toneAccent={t.accent}
              >
                <Box sx={{
                  // Comfortable reading measure INSIDE the full-width
                  // card so long paragraphs stay scannable while the
                  // card itself still stretches with the page.
                  maxWidth: 780,
                  fontSize: { xs: 14.5, md: 15.5 },
                  color: "#334155",
                  "& p":  { color: "#334155", lineHeight: 1.8, mb: 1.75 },
                  "& h1": { color: "#172033", fontWeight: 800, fontSize: 22, mt: 3, mb: 1.25, letterSpacing: "-0.01em" },
                  "& h2": { color: "#172033", fontWeight: 800, fontSize: 19, mt: 2.5, mb: 1, letterSpacing: "-0.01em" },
                  "& h3": { color: "#172033", fontWeight: 800, fontSize: 16.5, mt: 2, mb: 0.75 },
                  "& ul, & ol": { pl: 3.25, mb: 1.75 },
                  "& li": { mb: 0.6, lineHeight: 1.7 },
                  "& code": { px: 0.6, py: 0.15, borderRadius: 0.75, bgcolor: "#F1F5F9", color: "#0F172A", fontSize: "0.9em", fontFamily: "'SF Mono','JetBrains Mono','Roboto Mono',Menlo,Consolas,monospace" },
                  "& pre":  { p: 2, borderRadius: 2, bgcolor: "#0F172A", color: "#E2E8F0", overflow: "auto", mb: 2, fontSize: 13, lineHeight: 1.6 },
                  "& pre code": { bgcolor: "transparent", color: "inherit", p: 0 },
                  "& strong": { color: "#172033", fontWeight: 700 },
                  "& a":  { color: "#2563EB", textDecoration: "none", fontWeight: 600, "&:hover": { textDecoration: "underline" } },
                  "& blockquote": {
                    m: 0, my: 2, pl: 2, py: 0.5,
                    borderLeft: `3px solid ${t.accent}`,
                    color: "#334155", fontStyle: "italic", bgcolor: t.softBg, borderRadius: "0 8px 8px 0",
                  },
                  "& hr": { border: 0, borderTop: "1px solid #E5EAF1", my: 3 },
                  "& table": { width: "100%", borderCollapse: "collapse", mb: 2 },
                  "& th, & td": { border: "1px solid #E5EAF1", px: 1.25, py: 1, textAlign: "left", fontSize: 13.5 },
                  "& th": { bgcolor: "#F8FAFC", fontWeight: 800, color: "#172033" },
                  "& img": { maxWidth: "100%", borderRadius: 2, border: "1px solid #E5EAF1" },
                }}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{article.body_markdown as string}</ReactMarkdown>
                </Box>
              </SectionCard>
            )}
          </Box>

          {/* ══════════ RIGHT — sticky aside (desktop only) ══════════
              Sections list ("On this page") + article metadata. All
              rows come from the same data the sections themselves
              read — nothing is invented. Hidden on md and below;
              the article body already renders the same information
              in the hero meta row on small screens. */}
          {(jumpItems.length > 0 || updatedLabel) && (
            <Box sx={{ display: { xs: "none", lg: "block" } }}>
              <Box sx={{
                position: "sticky", top: 24,
                bgcolor: "#FFFFFF",
                borderRadius: 3,
                border: "1px solid #E5EAF1",
                boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 6px 16px rgba(15,23,42,0.04)",
                p: 2.25,
              }}>
                {jumpItems.length > 0 && (
                  <>
                    <Typography sx={{
                      fontSize: 11, fontWeight: 800, color: "#64748B",
                      textTransform: "uppercase", letterSpacing: "0.08em", mb: 1.25,
                    }}>
                      On this page
                    </Typography>
                    <Stack spacing={0}>
                      {jumpItems.map((j) => (
                        <Box
                          key={j.id}
                          component="a"
                          href={`#${j.id}`}
                          sx={{
                            display: "flex", alignItems: "center", gap: 1,
                            px: 1, py: 0.85, borderRadius: 1.25,
                            fontSize: 13.5, fontWeight: 600, color: "#172033",
                            textDecoration: "none",
                            transition: "background-color 140ms ease, color 140ms ease",
                            "&:hover": { bgcolor: t.softBg, color: t.text },
                            "& .aside-chevron": {
                              color: "#94A3B8",
                              transition: "color 140ms ease, transform 140ms ease",
                              ml: "auto",
                            },
                            "&:hover .aside-chevron": { color: t.accent, transform: "translateX(2px)" },
                          }}
                        >
                          <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: t.accent, flexShrink: 0 }} />
                          {j.label}
                          <ChevronRightRoundedIcon className="aside-chevron" sx={{ fontSize: 16 }} />
                        </Box>
                      ))}
                    </Stack>
                  </>
                )}

                {(updatedLabel || article.category || article.module) && (
                  <>
                    {jumpItems.length > 0 && <Divider sx={{ my: 1.75, borderColor: "#E5EAF1" }} />}
                    <Typography sx={{
                      fontSize: 11, fontWeight: 800, color: "#64748B",
                      textTransform: "uppercase", letterSpacing: "0.08em", mb: 1,
                    }}>
                      Article
                    </Typography>
                    <Stack spacing={0.75} sx={{ fontSize: 12.5 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                        <Box sx={{ color: "#64748B", fontWeight: 600 }}>Category</Box>
                        <Box sx={{ color: t.text, fontWeight: 700, textAlign: "right" }}>{article.category}</Box>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                        <Box sx={{ color: "#64748B", fontWeight: 600 }}>Module</Box>
                        <Box sx={{ color: "#172033", fontWeight: 700, textAlign: "right" }}>{article.module}</Box>
                      </Box>
                      {updatedLabel && (
                        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                          <Box sx={{ color: "#64748B", fontWeight: 600 }}>Last updated</Box>
                          <Box sx={{ color: "#172033", fontWeight: 700, textAlign: "right" }}>
                            {updatedLabel.replace(/^Updated /, "")}
                          </Box>
                        </Box>
                      )}
                    </Stack>
                  </>
                )}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

const VideoBox: React.FC<{ article: { video_url?: string | null; video_storage_path?: string | null; title: string } }> = ({ article }) => {
  const src = article.video_url ? article.video_url : (article.video_storage_path ? mediaUrl(article.video_storage_path) : "");
  const isExternalEmbed = /youtube\.com|youtu\.be|vimeo\.com/i.test(src);
  if (!src) return null;
  if (isExternalEmbed) {
    return <Box sx={{ position: "relative", pt: "56.25%" }}>
      <Box component="iframe" src={src} title={article.title} sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0, borderRadius: 2 }} allow="autoplay; encrypted-media; picture-in-picture" />
    </Box>;
  }
  return <Box component="video" controls src={src} sx={{ width: "100%", borderRadius: 2, bgcolor: "black" }} />;
};

export default HelpCenter;
